import { useState } from "react";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import { download } from "@/lib/api";
import { Panel } from "@/components/Common";
import { Button } from "@/components/ui/button";

const TEMPLATES = [
  ["classico", "Classico", "Riquadri e intestazione tabella piena"],
  ["moderno", "Moderno", "Fascia colorata in alto e totale evidenziato"],
  ["minimale", "Minimale", "Solo linee sottili, molto pulito"],
  ["elegante", "Elegante", "Carattere con grazie, titolo centrato"],
  ["griglia", "Griglia", "Fascia con logo e dati, riquadri e filigrana copia"],
  ["elettronica", "Fattura elettronica", "Fornitore e cliente affiancati, riepilogo IVA e calcolo fattura"],
];
const COLORS = ["#0F172A", "#0369A1", "#047857", "#B91C1C", "#7C2D12", "#4338CA"];

const Mock = ({ tpl, color }) => tpl === "elettronica" ? (
  <div className="h-24 rounded border border-slate-200 bg-white p-1.5">
    <div className="mb-1 grid grid-cols-2 gap-1"><div className="h-5 border-b border-slate-200" /><div className="h-5 bg-slate-100" /></div>
    <div className="mx-auto mb-1 h-1 w-1/3" style={{ background: color }} />
    <div className="mb-1 h-2 bg-slate-300" /><div className="mb-1 h-1 bg-slate-100" />
    <div className="grid grid-cols-2 gap-1"><div className="h-5 border border-slate-200" /><div className="flex flex-col justify-end"><div className="h-2 bg-slate-300" /></div></div>
  </div>
) : tpl === "griglia" ? (
  <div className="h-24 rounded border border-slate-200 bg-white p-1.5">
    <div className="mb-1 h-4 rounded-sm bg-slate-300" />
    <div className="mb-1 grid grid-cols-3 gap-px border border-slate-400 bg-slate-400"><div className="h-3 bg-white" /><div className="h-3 bg-white" /><div className="h-3 bg-white" /></div>
    <div className="relative h-8 border border-slate-400"><span className="absolute inset-0 flex items-center justify-center text-[7px] font-semibold text-slate-300">COPIA</span></div>
    <div className="mt-1 h-2 border border-slate-400" style={{ borderTopColor: color }} />
  </div>
) : (
  <div className="h-24 rounded border border-slate-200 bg-white p-2">
    <div className={`mb-1.5 h-3 rounded-sm ${tpl === "elegante" ? "mx-auto w-2/3" : "w-full"}`} style={tpl === "moderno" ? { background: color } : { borderBottom: `2px solid ${color}` }} />
    <div className="mb-1.5 grid grid-cols-2 gap-1">
      <div className={`h-5 ${tpl === "classico" ? "border border-slate-300" : "bg-slate-100"}`} />
      <div className={`h-5 ${tpl === "classico" ? "border border-slate-300" : "bg-slate-100"}`} />
    </div>
    <div className="h-2 rounded-sm" style={["classico", "moderno"].includes(tpl) ? { background: color } : { borderBottom: `1px solid ${color}` }} />
    <div className="mt-1 h-1 bg-slate-100" /><div className="mt-1 h-1 bg-slate-100" />
  </div>
);

export const PdfTemplatePanel = ({ c, set, className }) => {
  const [busy, setBusy] = useState(false);
  const tpl = c.pdf_template || "griglia";
  const color = c.pdf_color || "#0F172A";
  const preview = async () => {
    setBusy(true);
    try { await download("/company/pdf-preview", { template: tpl, color }); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Panel title="Modello fattura PDF" className={className}
      action={<Button size="sm" variant="outline" disabled={busy} data-testid="pdf-preview-button" onClick={preview}><FileDown className="mr-1 h-4 w-4" />Anteprima</Button>}>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {TEMPLATES.map(([k, name, desc]) => (
          <button key={k} type="button" data-testid={`pdf-template-${k}`} onClick={() => set("pdf_template", k)}
            className={`rounded-lg border p-2 text-left transition-colors ${tpl === k ? "border-sky-600 ring-2 ring-sky-100" : "border-slate-200 hover:border-slate-400"}`}>
            <Mock tpl={k} color={color} />
            <div className="mt-2 flex items-center gap-1 text-sm font-semibold">{tpl === k && <Check className="h-4 w-4 text-sky-600" />}{name}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </button>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 text-sm">
        <span className="mr-2 text-slate-500">Colore</span>
        {COLORS.map((col) => (
          <button key={col} type="button" data-testid={`pdf-color-${col.slice(1)}`} onClick={() => set("pdf_color", col)} aria-label={col}
            className={`h-7 w-7 rounded-full ${color === col ? "ring-2 ring-sky-500 ring-offset-2" : ""}`} style={{ background: col }} />
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">Vale per il PDF di cortesia di tutte le fatture, anche quelle ricevute (senza la tua intestazione) e le proforma. Salva le impostazioni per applicarlo; l'anteprima usa la scelta corrente.</p>
    </Panel>
  );
};
